import type { User } from '@auth/domain';
import type { RootState } from '@store';
import { useAppSelector } from '@store';
import { createContext, useContext, useEffect, type ReactNode } from 'react';

import { logger } from './index';

// Create context
const LoggerContext = createContext<typeof logger | null>(null);

export interface LoggerProviderProps {
  children: ReactNode;
}

export function LoggerProvider({ children }: LoggerProviderProps) {
  const user = useAppSelector((state: RootState) => state.auth.user) as User | null;

  // Set user ID when it changes
  useEffect(() => {
    if (user?.id) {
      logger.setUserId(user.id);
    }
  }, [user?.id]);

  // Log application initialization
  useEffect(() => {
    logger.debug('Application initialized', {
      appName: 'soteria-ui',
      version: import.meta.env.VITE_APP_VERSION || '0.1.0',
      environment: import.meta.env.MODE || 'development',
    });
  }, []);

  return <LoggerContext.Provider value={logger}>{children}</LoggerContext.Provider>;
}

// Hook to use logger
export function useLogger() {
  const loggerInstance = useContext(LoggerContext);

  if (!loggerInstance) {
    throw new Error('useLogger must be used within a LoggerProvider');
  }

  return loggerInstance;
}
